/**
 * The shape of one free tool's page, in one language.
 *
 * ── Why the copy is a typed object and not MDX ───────────────────────────────
 *
 * Every field below exists because something downstream reads it: `answer` is
 * what an assistant quotes, `faq` becomes both visible text and `FAQPage`
 * markup, `reading` is what turns a number into a sentence. Written as prose in
 * a markdown file, all of that becomes "the second paragraph, usually", and the
 * fifth language quietly loses a section nobody notices for a year.
 *
 * The types are also where the GEO research is enforced rather than remembered.
 * `answer` is capped by `content.test.ts` at the 134–167 word band that the
 * citation studies point at (see docs/SEO-GEO-RESEARCH.md, finding 4), because
 * a rule written only in a document is a rule that survives exactly as long as
 * the person who read it.
 */

import type { FaceMetrics } from "../face/metrics";

/**
 * Which measurement a tool leads with. `composite` is the whole panel.
 *
 * `attractiveness` is the SAME composite, read on a 10-point scale and with the
 * panel sorted into what helps and what holds it back. It is a second reading
 * of one number rather than a ninth measurement, and that is the point of it —
 * see the attractiveness-test entry in `lib/tools/index.ts` for why the query
 * deserves its own page and why that page must not invent a new score.
 */
export type ToolMetric =
  | "composite"
  | "attractiveness"
  | "faceShape"
  | "symmetry"
  | "goldenRatio"
  | "canthalTilt"
  | "jawline"
  | "fwhr"
  | "thirds"
  | "eyeSpacing"
  | "lipRatio";

/** The URL slugs, which are also the analytics event property. */
export const TOOL_SLUGS = [
  "looksmax-test",
  "attractiveness-test",
  "face-shape",
  "face-symmetry",
  "golden-ratio",
  "canthal-tilt",
  "jawline",
  "fwhr",
  "facial-thirds",
  "eye-spacing",
  "lip-ratio",
] as const;

export type ToolSlug = (typeof TOOL_SLUGS)[number];

export interface ToolFaq {
  readonly q: string;
  readonly a: string;
}

/**
 * One band of a measurement, and what it means in words.
 *
 * The tools show these instead of an adjective attached to a score, because an
 * adjective is not checkable and a range is. "0.72 — narrower than the
 * convention, which is where about a third of jaws sit" is a sentence a visitor
 * can argue with; "jawline: fair" is one they can only feel bad about.
 */
export interface ToolBand {
  /** Inclusive lower bound of the measured value. */
  readonly from: number;
  /** Exclusive upper bound. */
  readonly to: number;
  readonly label: string;
  readonly note: string;
}

export interface ToolCopy {
  /** `<title>`. Carries the query, not the brand-first phrasing. */
  readonly title: string;
  /** `<meta name="description">`. */
  readonly description: string;
  /** The visible H1. Usually the question, verbatim. */
  readonly h1: string;
  /** One grey line under the H1 saying why. Never a second paragraph. */
  readonly standfirst: string;
  /**
   * The answer capsule: 134–167 words, self-contained, leading with the answer.
   * This is the block written to be extracted with no surrounding context.
   */
  readonly answer: string;
  /** What the number is, in one sentence, shown next to the result. */
  readonly measures: string;
  /** How to read the result — 3 to 5 short lines. */
  readonly reading: readonly string[];
  /** Bands for the leading measurement, in ascending order. */
  readonly bands: readonly ToolBand[];
  readonly faq: readonly ToolFaq[];
  /** The one-line pitch under the result, into the app. */
  readonly upsell: string;
}

export interface Tool {
  readonly slug: ToolSlug;
  readonly metric: ToolMetric;
  /**
   * The unit the leading measurement is quoted in, used to format the number
   * and to choose the band. `composite` and `faceShape` have no single value.
   * `ten` is the composite divided by ten, to one decimal — "6.4 / 10" — the
   * scale people who type "rate my face out of 10" are asking in.
   */
  readonly unit: "score" | "ten" | "ratio" | "degrees" | "percent" | "shape";
  /** Which other tools to offer at the bottom. See lib/tools/index.ts. */
  readonly related: readonly ToolSlug[];
}

/** Pull the leading number out of a measured face for a given tool. */
export function leadingValue(
  metric: ToolMetric,
  metrics: FaceMetrics,
): number | null {
  switch (metric) {
    case "composite":
      return metrics.composite;
    case "attractiveness":
      // One decimal, rounded once here, so the band the page picks and the
      // number it prints can never disagree about which side of 7.0 a 69.96
      // composite sits on.
      return outOfTen(metrics.composite);
    case "faceShape":
      return null;
    default:
      return metrics[metric].value;
  }
}

/** The composite on a 10-point scale, to one decimal: 63.6 → 6.4. */
export function outOfTen(composite: number): number {
  return Math.round(composite) / 10;
}

/** Pull the 0–100 score for a given tool. */
export function leadingScore(metric: ToolMetric, metrics: FaceMetrics): number {
  switch (metric) {
    case "composite":
    case "attractiveness":
      return metrics.composite;
    case "faceShape":
      return metrics.faceShape.confidence;
    default:
      return metrics[metric].score;
  }
}

/** The band a measured value falls in, or null when it sits outside them all. */
export function bandFor(
  bands: readonly ToolBand[],
  value: number,
): ToolBand | null {
  return bands.find((b) => value >= b.from && value < b.to) ?? null;
}
